import React, { createContext, useContext, useState } from 'react';

// Define the type for an individual menu item
interface MenuItem {
  type: 'note' | 'bubble' | 'preview' | 'chart' | 'widget' | 'doc';
  data: any;
}

// Define the type for a position entry
interface PositionItem {
  position: 'bottomLeft' | 'bottomRight'; // Restrict positions to specific values
  data: MenuItem[];
}

// Define the context type for menu items
interface SelectedItemsContextType {
  minifiedMenuItems: PositionItem[];
  setMinifiedMenuItems: React.Dispatch<React.SetStateAction<PositionItem[]>>;
  addItemToPosition: (position: 'bottomLeft' | 'bottomRight', item: MenuItem) => void;
  removeMinifiedTab: (mode: MenuItem['type'], position: 'left' | 'right') => void;
  selectedTabLeft: number;
  setSelectedTabLeft: React.Dispatch<React.SetStateAction<number>>;
  selectedTabRight: number;
  setSelectedTabRight: React.Dispatch<React.SetStateAction<number>>;
}

// Define the context type for image preview data
interface MinifiedImgPreviewData {
  leftPanelId: { id: number; data: string; fileName: string; fileType: string; minPicId: number | null; maxPicId: number | null };
  rightPanelId: { id: number; data: string; fileName: string; fileType: string; minPicId: number | null; maxPicId: number | null };
}
interface MinifiedImgPreviewContextType {
  minifiedImgPreviewData: MinifiedImgPreviewData;
  updateLeftPanel: (data: Partial<MinifiedImgPreviewData['leftPanelId']>) => void;
  updateRightPanel: (data: Partial<MinifiedImgPreviewData['rightPanelId']>) => void;
}

// Combined context type
type CombinedContextType = SelectedItemsContextType & MinifiedImgPreviewContextType;

// Create the context
const CombinedContext = createContext<CombinedContextType | null>(null);

// Provider Component
export const MinifiedMenuItemsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for menu items
  const [minifiedMenuItems, setMinifiedMenuItems] = useState<PositionItem[]>([
    { position: 'bottomLeft', data: [] },
    { position: 'bottomRight', data: [] },
  ]);
  const [selectedTabLeft, setSelectedTabLeft] = useState<number>(0);
  const [selectedTabRight, setSelectedTabRight] = useState<number>(0);

  // State for image preview data
  const [minifiedImgPreviewData, setMinifiedImgPreviewData] = useState<MinifiedImgPreviewData>({
    leftPanelId: { id: 0, data: '', fileName: '', fileType: '', minPicId: 0, maxPicId: 0 },
    rightPanelId: { id: 0, data: '', fileName: '', fileType: '', minPicId: 0, maxPicId: 0 },
  });

  const addItemToPosition = (position: 'bottomLeft' | 'bottomRight', item: MenuItem) => {
    setMinifiedMenuItems((prev) => {
      const oppositePosition = position === 'bottomLeft' ? 'bottomRight' : 'bottomLeft';

      const updatedMenuItems = prev.map((entry) => {
        if (entry.position === position) {
          const existingIndex = entry.data.findIndex((existingItem) => existingItem.type === item.type);
          if (existingIndex !== -1) {
            const updatedData = [...entry.data];
            updatedData[existingIndex] = item;
            return { ...entry, data: updatedData };
          }
          return { ...entry, data: [...entry.data, item] };
        }

        if (entry.position === oppositePosition && item.type !== 'preview') {
          return { ...entry, data: entry.data.filter((existingItem) => existingItem.type !== item.type) };
        }

        return entry;
      });

      // Update the selected tab based on the new length
      const targetMenu = updatedMenuItems.find((entry) => entry.position === position);
      if (targetMenu) {
        const lastIndex = targetMenu.data.length - 1;
        if (position === 'bottomLeft') {
          setSelectedTabLeft(lastIndex);
        } else {
          setSelectedTabRight(lastIndex);
        }
      }

      return updatedMenuItems;
    });
  };

  const removeMinifiedTab = (mode: MenuItem['type'], position: 'left' | 'right') => {
    const mappedPosition = position === 'left' ? 'bottomLeft' : 'bottomRight';

    setMinifiedMenuItems((prev) =>
      prev.map((entry) =>
        entry.position === mappedPosition
          ? { ...entry, data: entry.data.filter((item) => item.type !== mode) }
          : entry
      )
    );
  };

  // Function to update the left panel data
  const updateLeftPanel = (data: Partial<MinifiedImgPreviewData['leftPanelId']>) => {
    setMinifiedImgPreviewData((prev) => ({
      ...prev,
      leftPanelId: { ...prev.leftPanelId, ...data },
    }));
  };

  // Function to update the right panel data
  const updateRightPanel = (data: Partial<MinifiedImgPreviewData['rightPanelId']>) => {
    setMinifiedImgPreviewData((prev) => ({
      ...prev,
      rightPanelId: { ...prev.rightPanelId, ...data },
    }));
  };

  return (
    <CombinedContext.Provider
      value={{
        minifiedMenuItems,
        setMinifiedMenuItems,
        addItemToPosition,
        removeMinifiedTab,
        minifiedImgPreviewData,
        updateLeftPanel,
        updateRightPanel,
        selectedTabLeft,
        setSelectedTabLeft,
        selectedTabRight,
        setSelectedTabRight,
      }}
    >
      {children}
    </CombinedContext.Provider>
  );
};

// Custom Hook
export const useMinifiedMenu = (): CombinedContextType => {
  const context = useContext(CombinedContext);
  if (!context) {
    throw new Error('useMinifiedMenu must be used within a CombinedProvider');
  }
  return context;
};
